/**
 * Фильтры и сортировка трекера задач (ТЗ 0062) — чистая модель, без React.
 *
 * Форма скопирована с `pages/FakturowniaExpenses/filtering.ts`: один плоский
 * объект «вида» + константа по умолчанию, фильтрация — последовательные
 * ранние возвраты внутри одного `.filter()`, сортировка — `switch` с
 * детерминированным тайбрейком.
 *
 * ⚠️ Про ручной порядок. `manual` — полноправное значение сортировки и
 * значение по умолчанию, и означает оно «порядок сервера, ничего не делать».
 * `sortTasks` при нём возвращает исходный массив КАК ЕСТЬ: `taskListModel`
 * держится на том, что фид приходит в порядке `position`, а стабильная
 * сортировка его сохраняет. Любая другая сортировка обязана работать по копии
 * (`slice()`), иначе она перепишет массив, который отдал Apollo.
 */
import { IStaffTask, isClosed } from './taskBuckets';
import { TTaskFilter, urgencyOf } from './taskListModel';

/**
 * Виды доски В ПОРЯДКЕ ПОКАЗА (ТЗ 0064): канбан первым, список последним.
 *
 * Владелец: «в начале это всё-таки пусть будет канбан-доска, а спискам пусть
 * будут в конце». «Сроки» — тоже канбан, и он же остаётся стартовым
 * (`DEFAULT_TASK_VIEW`, ТЗ 0063): человек открывает командный центр, чтобы
 * увидеть, что горит.
 *
 * Порядок здесь — единственный источник ПОКАЗА. Вид по умолчанию задаёт не он:
 * на дашборде это `DEFAULT_TASK_VIEW`, у встроенных досок — первый из
 * разрешённых им `availableViews`.
 */
export const TASK_VIEWS = [
  'time',
  'status',
  'gantt',
  'calendar',
  'list',
] as const;
export type TTaskView = typeof TASK_VIEWS[number];

/** Статусы, доступные как чекбоксы фильтра (в отличие от колонок канбана —
 *  там `cancelled` живёт вместе с `done`). */
export const TASK_STATUS_FILTERS = [
  'open',
  'in_progress',
  'done',
  'cancelled',
] as const;

export const TASK_SORT_KEYS = [
  'manual',
  'due',
  'start',
  'priority',
  'status',
  'title',
  'assignee',
  'created',
  'updated',
] as const;
export type TTaskSortKey = typeof TASK_SORT_KEYS[number];

export interface ITasksSort {
  key: TTaskSortKey;
  dir: 'asc' | 'desc';
}

export const DEFAULT_TASKS_SORT: ITasksSort = { key: 'manual', dir: 'asc' };

export interface ITasksView {
  /** Чипы срочности — быстрый фильтр, он же цель клика по метрике. */
  quick: TTaskFilter;
  statuses: string[];
  priorities: string[];
  /** Идентификаторы исполнителей; пусто — любые. Только командный режим. */
  assigneeIds: string[];
  dueFrom: string;
  dueTo: string;
  search: string;
  /** Показывать закрытые в списке (иначе только свежий хвост). */
  withClosed: boolean;
  fromMessageOnly: boolean;
  /** '' | 'student' | 'group' | 'teacher' | 'none' */
  subjectKind: string;
  /** Связанные сущности (ТЗ 0064). Внутри вида — ИЛИ, между видами — И;
   *  фильтр СЕРВЕРНЫЙ, иначе счётчик в шапке разойдётся со списком. */
  links: ITaskLinkRef[];
}

/** Ссылка на объект в фильтре. `name` нужен крошке; при заходе по чужой
 *  ссылке его ещё нет — тогда показывается id, а не пустота. */
export interface ITaskLinkRef {
  kind: string;
  id: string;
  name?: string;
}

export const DEFAULT_TASKS_VIEW: ITasksView = {
  quick: 'all',
  statuses: [],
  priorities: [],
  assigneeIds: [],
  dueFrom: '',
  dueTo: '',
  search: '',
  links: [],
  withClosed: false,
  fromMessageOnly: false,
  subjectKind: '',
};

const CLOSED_STATUSES = ['done', 'cancelled'];

/**
 * Привести вид к согласованному состоянию. Два инварианта, каждый из которых
 * иначе даёт пустой экран без объяснения:
 *
 * 1. выбранный `done`/`cancelled` обязан включить `withClosed` — иначе фильтр
 *    просит закрытые у фида, который их не содержит;
 * 2. диапазон дат и чип «без срока» взаимоисключающи — серверный диапазон
 *    ИСКЛЮЧАЕТ задачи без срока, так что вместе они всегда дают ноль.
 */
export const normalizeTasksView = (view: ITasksView): ITasksView => {
  const statuses = Array.from(new Set(view.statuses)).sort();
  const priorities = Array.from(new Set(view.priorities)).sort();
  const assigneeIds = Array.from(new Set(view.assigneeIds)).sort();
  const wantsClosed = statuses.some((s) => CLOSED_STATUSES.includes(s));
  const hasRange = Boolean(view.dueFrom || view.dueTo);
  let { dueFrom, dueTo } = view;
  if (dueFrom && dueTo && dueFrom > dueTo) {
    [dueFrom, dueTo] = [dueTo, dueFrom];
  }
  return {
    ...view,
    statuses,
    priorities,
    assigneeIds,
    dueFrom,
    dueTo,
    quick: hasRange && view.quick === 'noDate' ? 'all' : view.quick,
    withClosed: view.withClosed || wantsClosed,
  };
};

/** Сколько ограничений включено — счётчик на кнопке «Фильтры». */
export const activeFilterCount = (view: ITasksView): number => {
  let n = 0;
  if (view.statuses.length) n += 1;
  if (view.priorities.length) n += 1;
  if (view.assigneeIds.length) n += 1;
  if (view.dueFrom || view.dueTo) n += 1;
  if (view.subjectKind) n += 1;
  // Весь набор связанных сущностей — ОДНА категория, как статусы или
  // исполнители: иначе выбор трёх групп превращает «Фильтры (2)» в
  // «Фильтры (4)» и число перестаёт значить «сколько условий наложено».
  if (view.links.length) n += 1;
  if (view.fromMessageOnly) n += 1;
  return n;
};

/**
 * «Есть ли у задачи связь такого вида» — и ничего больше.
 *
 * ⚠️ Раньше здесь стоял `subjectKindOf`, отдающий ОДИН вид по приоритету
 * (преподаватель → студент → группа), а фильтр сравнивал на равенство. Пока
 * виды были взаимоисключающими (до 0064), это было верно. Потом задача
 * научилась касаться и группы, и преподавателя разом — и фильтр «Группа»
 * перестал её показывать. Он же считал «без привязки» задачу, у которой есть
 * курс и вакансия, потому что про эти виды просто не знал.
 */
const hasLinkOfKind = (task: IStaffTask, kind: string): boolean => {
  const links = task.links || [];
  if (kind === 'none') return links.length === 0;
  return links.some((link) => link.kind === kind);
};

/**
 * Клиентские предикаты поверх уже суженного сервером фида. Здесь остаётся
 * только то, что сервер не умеет или что зависит от ЛОКАЛЬНОГО «сегодня»
 * браузера: у сервера своя таймзона, и чип «сегодня» обязан считаться по
 * часам оператора.
 */
export const filterTasks = (
  tasks: IStaffTask[],
  view: ITasksView,
  today: string,
): IStaffTask[] =>
  tasks.filter((task) => {
    if (view.fromMessageOnly && !task.fromMessage) return false;
    if (view.subjectKind && !hasLinkOfKind(task, view.subjectKind)) {
      return false;
    }
    if (view.quick !== 'all') {
      const urgency = urgencyOf(task, today);
      if (
        view.quick === 'overdue' &&
        !(urgency === 'overdue' && !isClosed(task))
      ) {
        return false;
      }
      if (view.quick === 'today' && urgency !== 'today') return false;
      if (view.quick === 'noDate' && urgency !== 'noDate') return false;
    }
    return true;
  });

const PRIORITY_RANK: Record<string, number> = { high: 0, normal: 1, low: 2 };
const STATUS_RANK: Record<string, number> = {
  open: 0,
  in_progress: 1,
  done: 2,
  cancelled: 3,
};

/**
 * Тайбрейк ручным рангом — он ВОСПРОИЗВОДИТ порядок сервера внутри равных
 * ключей («тот же приоритет — оставь мой порядок»), а не спорит с ним.
 * Направление НЕ инвертируем: иначе «по приоритету ↓» ещё и перевернул бы
 * ручной порядок внутри каждой группы.
 */
const tieBreak = (a: IStaffTask, b: IStaffTask): number => {
  const pa = a.position ?? Number.MAX_SAFE_INTEGER;
  const pb = b.position ?? Number.MAX_SAFE_INTEGER;
  if (pa !== pb) return pa - pb;
  return a.id.localeCompare(b.id);
};

/** Значение, по которому сравниваем. `null` — «пусто», такие строки всегда
 *  уезжают в конец НЕЗАВИСИМО от направления. */
const valueOf = (
  task: IStaffTask,
  key: TTaskSortKey,
): string | number | null => {
  switch (key) {
    case 'due':
      return task.dueDate || null;
    case 'start':
      return task.startDate || null;
    case 'priority':
      return PRIORITY_RANK[task.priority] ?? 9;
    case 'status':
      return STATUS_RANK[task.status] ?? 9;
    case 'title':
      return task.title;
    case 'assignee':
      return task.assigneeName || null;
    case 'created':
      return task.createdAt || null;
    case 'updated':
      return task.updatedAt || null;
    default:
      return null;
  }
};

export const sortTasks = (
  tasks: IStaffTask[],
  sort: ITasksSort,
): IStaffTask[] => {
  // Ручной порядок — это порядок сервера. Не трогаем массив вовсе.
  if (sort.key === 'manual') return tasks;

  const dir = sort.dir === 'asc' ? 1 : -1;
  return tasks.slice().sort((a, b) => {
    const va = valueOf(a, sort.key);
    const vb = valueOf(b, sort.key);
    // Пустые в конец — и это НЕ умножается на направление: задача без срока
    // не должна возглавлять ни «по сроку ↑», ни «по сроку ↓».
    if (va === null || vb === null) {
      if (va === vb) return tieBreak(a, b);
      return va === null ? 1 : -1;
    }
    let r = 0;
    if (typeof va === 'number' && typeof vb === 'number') r = va - vb;
    else r = String(va).localeCompare(String(vb));
    if (r !== 0) return r * dir;
    return tieBreak(a, b);
  });
};

/** Опции фильтра исполнителей, выведенные из загруженных строк, — без
 *  отдельного запроса и без людей, у которых в этом фиде задач нет. */
export const assigneeOptionsFromRows = (
  tasks: IStaffTask[],
): Array<{ id: string; name: string }> => {
  const seen = new Map<string, string>();
  tasks.forEach((task) => {
    if (task.assigneeId && !seen.has(task.assigneeId)) {
      seen.set(task.assigneeId, task.assigneeName || task.assigneeId);
    }
  });
  const options = Array.from(seen.entries())
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name) || a.id.localeCompare(b.id));

  // Тёзки. В школе их достаточно, и две одинаковые кнопки «Ковальская Анна» —
  // это фильтр, про который нельзя сказать, кого он выберет. Различаем номером:
  // почты и других опознавательных полей у `StaffTaskType` нет намеренно (это
  // ПДн), а номер сотрудника и так стоит в ссылке.
  const byName = new Map<string, number>();
  options.forEach((o) => byName.set(o.name, (byName.get(o.name) || 0) + 1));
  return options.map((o) =>
    (byName.get(o.name) || 0) > 1 ? { ...o, name: `${o.name} #${o.id}` } : o,
  );
};

export interface IAssigneeDirectoryEntry {
  id: string;
  name: string;
  email: string;
}

/**
 * Варианты фильтра «исполнители» — ОБЪЕДИНЕНИЕ справочника сотрудников и
 * загруженных строк.
 *
 * Почему не только строки: сервер уже отдал задачи ОДНОГО выбранного человека,
 * и список схлопывается до него же — второго исполнителя не добавить и
 * переключиться не на кого. Фильтр становится дорогой в один конец.
 *
 * Почему не только справочник: он скоуплен по юрлицу, а лента задач — нет.
 * Исполнитель чужого юрлица в ленте виден, и не дать отфильтровать по нему
 * значило бы показывать строки, которые нечем отобрать.
 *
 * Тёзки разводятся почтой: она точнее номера и уже показывается сотруднику в
 * существующих списках исполнителей. У человека, пришедшего только из строки,
 * почты нет — он остаётся под своим именем.
 */
export const assigneeFilterOptions = (
  directory: IAssigneeDirectoryEntry[],
  tasks: IStaffTask[],
): Array<{ id: string; name: string }> => {
  const byId = new Map<string, string>();
  const emailById = new Map<string, string>();
  directory.forEach((u) => {
    byId.set(u.id, u.name || u.email);
    if (u.email) emailById.set(u.id, u.email);
  });
  assigneeOptionsFromRows(tasks).forEach((o) => {
    // `#id` из запасного разведения тёзок здесь лишний: ниже разводим почтой.
    if (!byId.has(o.id)) byId.set(o.id, o.name.replace(/ #\d+$/, ''));
  });

  const nameCount = new Map<string, number>();
  Array.from(byId.values()).forEach((n) =>
    nameCount.set(n, (nameCount.get(n) || 0) + 1),
  );
  return Array.from(byId.entries())
    .map(([id, name]) => {
      const email = emailById.get(id);
      return {
        id,
        name:
          (nameCount.get(name) || 0) > 1 && email
            ? `${name} \u00b7 ${email}`
            : name,
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name) || a.id.localeCompare(b.id));
};
